import { IPhoneNumber } from '../interface'
import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { Message01Icon } from '@forwardflow/icons'
import Icon from '@forwardflow/ui/Icons'
import cn from 'classnames'

const MessageCount = ({ count = 0 }: { count: number }) => {
   return (
      <div className="flex items-center gap-x-2 rounded-[4px] bg-[#9061DA]/[0.05] px-2 py-1">
         <Message01Icon className="text-white w-4 h-4" />
         <p className="text-white text-sm font-medium">{count}</p>
      </div>
   )
}

export const NumberItem = ({
   selectMode = false,
   onSelect = () => {},
   children = null,
   number,
   onDelete = undefined,
   onAssign = undefined,
   showAssignButton = false,
   showMessageCount = true,
   isSelected = false,
   checkboxMode = false,
   isChecked = false,
   onCheckboxChange = undefined,
   anyItemChecked = false,
}: {
   selectMode?: boolean
   onSelect?: (number: IPhoneNumber) => void
   children?: React.ReactNode
   number: IPhoneNumber
   onDelete?: (number: IPhoneNumber) => void
   onAssign?: (number: IPhoneNumber) => void
   showAssignButton?: boolean
   showMessageCount?: boolean
   isSelected?: boolean
   checkboxMode?: boolean
   isChecked?: boolean
   onCheckboxChange?: (number: IPhoneNumber) => void
   anyItemChecked?: boolean
}) => {
   const [isHovered, setIsHovered] = useState(false)
   const isInactive = number?.campaignCount === 0 || !number?.campaignCount
   const showCheckbox = checkboxMode && (isHovered || anyItemChecked)

   return (
      <div
         className={cn(
            'flex items-center justify-between bg-[#9061DA]/10 py-3 px-8'
         )}
         onMouseEnter={() => setIsHovered(true)}
         onMouseLeave={() => setIsHovered(false)}
      >
         <div className="flex items-center gap-x-3 flex-1">
            {checkboxMode && (
               <motion.div
                  animate={{ opacity: showCheckbox ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center w-5"
               >
                  <button
                     onClick={() => onCheckboxChange?.(number)}
                     className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isChecked
                           ? 'bg-[#9061DA] border-[#9061DA]'
                           : 'bg-transparent border-white/30 hover:border-white/50'
                     }`}
                     style={{ visibility: showCheckbox ? 'visible' : 'hidden' }}
                  >
                     {isChecked && (
                        <Icon
                           name="Check"
                           className="fill-white"
                           width={12}
                           height={12}
                        />
                     )}
                  </button>
               </motion.div>
            )}
            <div className="flex flex-col gap-y-1">
               <p className="font-medium text-white">
                  {number?.phoneNumber?.friendlyName}
               </p>
               <p className="text-white/70 text-sm">
                  {number?.phoneNumber?.locality || 'Unknown'}
               </p>
            </div>
         </div>
         {!anyItemChecked && (
            <div className="flex items-center gap-x-2">
               <AnimatePresence mode="wait">
                  {isInactive && isHovered && showAssignButton && onAssign && (
                     <motion.button
                        key="assign-button"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-black/20 rounded-[4px] px-4 py-2 text-white hover:text-white/70 text-sm font-medium hover:bg-black/30 transition-colors"
                        onClick={() => {
                           onAssign?.(number)
                        }}
                     >
                        Assign to Campaign
                     </motion.button>
                  )}
                  {showMessageCount && !isHovered && (
                     <motion.div
                        key="message-count"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2 }}
                     >
                        <MessageCount
                           count={number?.conversationStats?.totalMessages || 0}
                        />
                     </motion.div>
                  )}
                  {selectMode && isSelected && !isHovered && (
                     <div className="w-6 h-6 rounded-full flexc bg-primary group-hover:hidden">
                        <Icon
                           name="Check"
                           className="fill-white"
                           width={12}
                           height={12}
                        />
                     </div>
                  )}
                  {onDelete && number?.phoneNumber?.removeStatus ? (
                     <motion.div
                        key="remove-status"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="rounded-[4px] px-3 py-1.5 text-xs font-medium bg-[#C11574]/20 text-[#FF6B9D] border border-[#C11574]/30"
                     >
                        {number.phoneNumber.removeStatus === 'queued' ? 'Queued for removal' : 'Pending for removal'}
                     </motion.div>
                  ) : onDelete && isHovered && (
                     <motion.button
                        key="delete-button"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                           onDelete?.(number)
                        }}
                        className="bg-black/20 rounded-[4px] px-2 py-2 text-white hover:text-white/70 text-sm font-medium hover:bg-black/30 transition-colors"
                     >
                        <Icon
                           name="Delete"
                           className="fill-[#FF6B9D]"
                           width={20}
                           height={20}
                        />
                     </motion.button>
                  )}
                  {selectMode && isHovered && (
                     <div>
                        {isSelected ? (
                           <button
                              onClick={() => {
                                 onSelect(number)
                              }}
                              className="h-10 ml-2 px-3 group-hover:block transition-all content-end border rounded w-28 border-[#C11574] bg-[#C11574]/20 hover:bg-[#C11574]/40"
                           >
                              Remove
                           </button>
                        ) : (
                           <button
                              onClick={() => {
                                 onSelect(number)
                              }}
                              className="w-24 h-10 transition-all border rounded group-hover:block border-primary bg-primary/20 hover:bg-primary"
                           >
                              Select
                           </button>
                        )}
                     </div>
                  )}

                  {children}
               </AnimatePresence>
            </div>
         )}
      </div>
   )
}
